import type { TemplateNode, TemplatePage } from '@ulysses-inc/harami_api_client'
import { UUID } from 'src/exShared/util/uuid'
import { MigrationDomainError, type UuidMappings } from './types'

export const flattenNodes = (pages: TemplatePage[]): TemplateNode[] => {
  const result: TemplateNode[] = []
  const visit = (nodes: TemplateNode[] | undefined) => {
    for (const node of nodes ?? []) {
      result.push(node)
      visit(node.nodes)
    }
  }
  for (const page of pages) visit(page.nodes)
  return result
}

export const createUuidMappings = (
  pages: TemplatePage[],
  createUuid: () => string = UUID,
): UuidMappings => {
  const nodes = flattenNodes(pages)
  const sourceUuids = new Set(nodes.map(node => node.uuid))
  const allocated = new Set<string>()
  const nodeUuids = new Map<string, string>()

  const allocate = (sourceUuid: string, label: string) => {
    const targetUuid = createUuid()
    if (
      targetUuid.trim() === '' ||
      targetUuid === sourceUuid ||
      sourceUuids.has(targetUuid) ||
      allocated.has(targetUuid)
    ) {
      throw new MigrationDomainError([
        `${label}の新しいUUIDを一意に採番できませんでした。`,
      ])
    }
    allocated.add(targetUuid)
    return targetUuid
  }

  for (const node of nodes) {
    if (node.uuid.trim() === '' || nodeUuids.has(node.uuid)) {
      throw new MigrationDomainError([
        'Node UUIDが空、またはひな形内で重複しています。',
      ])
    }
    nodeUuids.set(node.uuid, allocate(node.uuid, 'Node'))
  }

  return { nodeUuids }
}

export const createTemporaryIdAllocator = (
  pages: TemplatePage[],
): (() => number) => {
  const usedIds = new Set<number>()
  for (const page of pages) {
    if (page.id !== undefined) usedIds.add(page.id)
  }
  for (const node of flattenNodes(pages)) usedIds.add(node.id)

  let nextId = -1
  return () => {
    while (usedIds.has(nextId)) nextId -= 1
    const allocated = nextId
    usedIds.add(allocated)
    nextId -= 1
    return allocated
  }
}
